import { hasScope } from '@nyce/admin-kit/api';
import { Chip } from '@nyce/admin-kit/ui';
import { SCOPES } from '../api';
import { useHubLogin } from '../session';
import { useTargets } from '../identity/session';
import { formatRelative } from '../lib/format';
import type { Agent, AgentInfo, AutomationGwApi, TunnelStatus } from './api/client';

/** The environment picked in the shell (`?target=`). */
export const useAutomationTarget = () => useTargets().active;

/** Same page, same environment: links keep `?target=`. */
export const agPath = (path: string, target: string) =>
  `${path}${path.includes('?') ? '&' : '?'}target=${encodeURIComponent(target)}`;

export function useAutomationWrite(): boolean {
  return hasScope(useHubLogin()?.scopes, SCOPES.automationWrite);
}

/** `1.10.2` > `1.9.0`; non-numeric parts compare as text. */
export function compareVersions(a: string, b: string): number {
  const pa = a.replace(/^v/i, '').split(/[.+-]/);
  const pb = b.replace(/^v/i, '').split(/[.+-]/);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const x = pa[i] ?? '0';
    const y = pb[i] ?? '0';
    const nx = Number(x);
    const ny = Number(y);
    const d = Number.isFinite(nx) && Number.isFinite(ny) ? nx - ny : x.localeCompare(y);
    if (d) return Math.sign(d);
  }
  return 0;
}

export const isOutdated = (version: string | null | undefined, latest: string | null | undefined) =>
  !!version && !!latest && compareVersions(version, latest) < 0;

export function ConnectionChip({ t }: { t: TunnelStatus | undefined }) {
  if (!t?.isConnected)
    return (
      <Chip size="sm" className="oph-status nyk-tone-red" data-state="offline">
        offline
      </Chip>
    );
  return (
    <Chip
      size="sm"
      className="oph-status nyk-tone-green"
      data-state="connected"
      title={t.connectedAt ? `since ${new Date(t.connectedAt).toLocaleString()}` : undefined}
    >
      connected{t.connectedAt ? ` ${formatRelative(t.connectedAt, Date.now(), '').replace(/ ago$/, '')}` : ''}
    </Chip>
  );
}

export function VersionCell({
  version,
  latest,
}: {
  version: string | null | undefined;
  latest?: string | null;
}) {
  if (!version) return <span className="nyk-ui-hint">—</span>;
  return (
    <span>
      {version}
      {isOutdated(version, latest) && (
        <>
          {' '}
          <Chip size="sm" className="oph-status nyk-tone-amber" title={`Latest is ${latest}`}>
            update
          </Chip>
        </>
      )}
    </span>
  );
}

/** System info of every connected agent (the gateway answers per agent), a few at a time; missing = null. */
export async function agentInfos(
  api: AutomationGwApi,
  tunnels: TunnelStatus[],
  parallel = 6
): Promise<Map<string, AgentInfo | null>> {
  const ids = tunnels.filter((t) => t.isConnected).map((t) => t.clientId);
  const out = new Map<string, AgentInfo | null>();
  let next = 0;
  const worker = async () => {
    while (next < ids.length) {
      const id = ids[next++];
      out.set(id, await api.info(id).catch(() => null));
    }
  };
  await Promise.all(Array.from({ length: Math.min(parallel, ids.length) }, worker));
  return out;
}

/** `wh-01` → the agent's tunnel row (case-insensitive like the gateway). */
export const tunnelOf = (tunnels: TunnelStatus[] | null | undefined, clientId: string) =>
  tunnels?.find((t) => t.clientId.toLowerCase() === clientId.toLowerCase());

/** How an agent signs in: its mode, the method it last used, and an AUTH error if its token failed. */
export function AuthChip({ a }: { a: Agent }) {
  const mode = a.authMode ?? 'legacy';
  const [tone, label] =
    mode === 'auth'
      ? ['green', 'AUTH only']
      : mode === 'both'
        ? a.lastAuthMethod === 'bearer'
          ? ['green', 'AUTH']
          : ['amber', 'enrolled']
        : ['grey', 'secret'];
  return (
    <span>
      <Chip
        size="sm"
        className={`oph-status nyk-tone-${tone}`}
        data-auth={mode}
        title={
          mode === 'legacy'
            ? 'Shared secret only'
            : `Mode ${mode}; last connect with ${a.lastAuthMethod ?? 'unknown'}${a.lastBearerAt ? `, AUTH token ${formatRelative(a.lastBearerAt)}` : ''}`
        }
      >
        {label}
      </Chip>
      {a.lastAuthError && <div className="nyk-text-red nyk-wrap">{a.lastAuthError}</div>}
    </span>
  );
}

/** Whether new workers (and AUTH) reach the agent without a site visit, and whether its config would survive an update. */
export function RemoteCell({ a }: { a: Agent }) {
  const stuck = a.remoteUpdatable === false;
  return (
    <span>
      {a.remoteUpdatable == null ? (
        <span className="nyk-ui-hint">unknown</span>
      ) : (
        <Chip
          size="sm"
          className={`oph-status nyk-tone-${stuck ? 'red' : 'green'}`}
          data-remote={stuck ? 'no' : 'yes'}
          title={
            stuck
              ? a.lastPlatform === 'container'
                ? 'Runs in a container: redeploy it with AGENT__AUTH__* to move it'
                : 'No bootstrapper: needs a site visit to update'
              : 'Has a bootstrapper: updates and AUTH reach it remotely'
          }
        >
          {stuck ? 'site visit' : 'remote'}
        </Chip>
      )}
      {a.lastPlatform && <span className="nyk-ui-hint"> {a.lastPlatform}</span>}
      {a.configSource === 'current' && (
        <div
          className="nyk-text-amber"
          title="Its config lives only in current/ and would be lost on the next update"
        >
          config at risk
        </div>
      )}
    </span>
  );
}

/** Agent states for filters and dashboards. Containers cannot keep pushed credentials: they get theirs in env. */
export const canMoveToAuth = (a: Agent, connected: boolean) =>
  (a.authMode ?? 'legacy') === 'legacy' &&
  connected &&
  a.lastPlatform !== 'container' &&
  a.remoteUpdatable !== false &&
  a.configSource !== 'current';

export const isContainer = (a: Agent) => a.lastPlatform === 'container';
